import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { UserPlus, Users, CreditCard, Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const Membership = () => {
  const { user, isMember } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [checkingMembership, setCheckingMembership] = useState(true);

  useEffect(() => {
    const checkExistingMembership = async () => {
      if (!user) {
        setCheckingMembership(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("members")
          .select("id, membership_status")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          // User already has a membership (pending or active), redirect to member portal
          navigate("/member-portal");
        }
      } catch (error: any) {
        console.error("Error checking membership:", error);
      } finally {
        setCheckingMembership(false);
      }
    };

    checkExistingMembership();
  }, [user, navigate]);

  if (checkingMembership) {
    return (
      <section className="py-16 px-4 bg-background">
        <div className="container mx-auto max-w-4xl">
          <p className="text-center">Checking membership status...</p>
        </div>
      </section>
    );
  }


  const benefits = [
    {
      icon: Users,
      title: t("membership.benefit1.title"),
      description: t("membership.benefit1.description"),
    },
    {
      icon: Heart,
      title: t("membership.benefit2.title"),
      description: t("membership.benefit2.description"),
    },
    {
      icon: CreditCard,
      title: t("membership.benefit3.title"),
      description: t("membership.benefit3.description"),
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {t("membership.title")}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t("membership.subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
          {benefits.map((benefit, index) => (
            <Card key={index} className="text-center">
              <CardHeader>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4 mx-auto">
                  <benefit.icon className="w-8 h-8 text-primary" />
                </div>
                <CardTitle>{benefit.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  {benefit.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center">
          {!user ? (
            <div className="space-y-4">
              <p className="text-lg text-muted-foreground mb-4">
                {t("membership.notloggedin")}
              </p>
              <div className="flex gap-4 justify-center">
                <Button asChild size="lg">
                  <Link to="/auth">
                    <UserPlus className="mr-2 h-5 w-5" />
                    {t("membership.signinsignup")}
                  </Link>
                </Button>
              </div>
            </div>
          ) : isMember ? (
            <Button asChild size="lg">
              <Link to="/member-portal">
                {t("membership.gotoportal")}
              </Link>
            </Button>
          ) : (
            <div className="space-y-4">
              <p className="text-lg text-muted-foreground mb-4">
                {t("membership.readytojoin")}
              </p>
              <Button asChild size="lg">
                <Link to="/member-registration">
                  <UserPlus className="mr-2 h-5 w-5" />
                  {t("membership.becomemember")}
                </Link>
              </Button>
            </div>
          )}
        </div>

        {user && !isMember && (
          <div className="mt-8 max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>{t("membership.whattoexpect")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-muted-foreground">
                <p>• {t("membership.step1")}</p>
                <p>• {t("membership.step2")}</p>
                <p>• {t("membership.step3")}</p>
                <p>• {t("membership.step4")}</p>
                <p>• {t("membership.step5")}</p>
                <p>• {t("membership.step6")}</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default Membership;
